/**
 * Demo Transport
 *
 * A transport wrapper that intercepts Connect-RPC calls and returns mock data
 * when demo mode is active. This allows the app to run without a backend for
 * screenshot and marketing purposes.
 */

import { createConnectTransport, type ConnectTransportOptions } from '@connectrpc/connect-web'
import { type Transport, type Interceptor } from '@connectrpc/connect'
import { isDemoModeActive } from './demo-mode'
import {
  demoUser,
  demoOrganization,
  demoOrganizationUsers,
  demoContacts,
  demoInquiries,
  demoBookings,
  demoEvents,
  demoInvoices,
  demoQuotes,
  demoTasks,
  demoItems,
  demoTaxRates,
  demoDiscounts,
  demoNotes,
  demoFeedback,
  demoWorkflowDefinitions,
} from './demo-data'

// Method name to mock response mapping
type MockHandler = (request: unknown) => unknown

const mockHandlers: Record<string, MockHandler> = {
  // User endpoints
  'cortado.v1.CortadoService/GetMe': () => ({
    user: demoUser,
  }),

  // Organization endpoints
  'cortado.v1.CortadoService/ListMyOrganizations': () => ({
    organizations: [demoOrganization],
  }),

  'cortado.v1.CortadoService/GetOrganization': () => ({
    organization: demoOrganization,
  }),

  'cortado.v1.CortadoService/ListOrganizationUsers': () => ({
    users: demoOrganizationUsers,
    totalCount: demoOrganizationUsers.length,
  }),

  // Workflow endpoints
  'cortado.v1.CortadoService/ListWorkflowDefinitions': () => ({
    workflowDefinitions: demoWorkflowDefinitions,
    totalCount: demoWorkflowDefinitions.length,
  }),

  'cortado.v1.CortadoService/UpdateWorkflowDefinition': (req: unknown) => {
    const request = req as { id?: string; enabled?: boolean }
    const workflow = demoWorkflowDefinitions.find((item) => item.id === request.id)

    if (workflow && !workflow.stock && request.enabled !== undefined) {
      workflow.enabled = request.enabled
    }

    return {
      workflowDefinition: workflow ?? demoWorkflowDefinitions[0],
    }
  },

  'cortado.v1.CortadoService/GetOrganizationOnboarding': () => ({
    onboarding: {
      steps: [],
      completedSteps: 5,
      totalSteps: 5,
      completionRate: 100,
      isCompleted: true,
    },
  }),

  // Settings
  'cortado.v1.CortadoService/GetSystemSettings': () => ({
    settings: {
      maintenanceMode: false,
      version: '1.0.0-demo',
    },
  }),

  // Contact endpoints
  'cortado.v1.CortadoService/ListContacts': () => ({
    contacts: demoContacts,
    totalCount: demoContacts.length,
  }),

  'cortado.v1.CortadoService/GetContact': (req: unknown) => {
    const request = req as { id?: string }
    const contact = demoContacts.find((c) => c.id === request.id) || demoContacts[0]
    return { contact }
  },

  // Inquiry/Lead endpoints
  'cortado.v1.CortadoService/ListInquiries': () => {
    // Convert full Inquiry objects to InquiryLite format
    const inquiriesLite = demoInquiries.map((inquiry) => ({
      id: inquiry.id,
      organizationId: inquiry.organizationId,
      stage: inquiry.stage,
      eventDate: inquiry.eventDate,
      eventLength: inquiry.eventLength,
      eventGuests: inquiry.eventGuests,
		eventTypeKey: inquiry.eventType,
      contactId: inquiry.contactId,
      contactFirstName: inquiry.contact?.firstName || '',
      contactLastName: inquiry.contact?.lastName || '',
      quoteTotal: inquiry.quoteTotal,
      createdAt: inquiry.createdAt,
      updatedAt: inquiry.updatedAt,
    }))
    return {
      inquiries: inquiriesLite,
      totalCount: inquiriesLite.length,
    }
  },

  'cortado.v1.CortadoService/ListOrganizationInquiries': () => {
    // Convert full Inquiry objects to InquiryLite format
    const inquiriesLite = demoInquiries.map((inquiry) => ({
      id: inquiry.id,
      organizationId: inquiry.organizationId,
      stage: inquiry.stage,
      eventDate: inquiry.eventDate,
      eventLength: inquiry.eventLength,
      eventGuests: inquiry.eventGuests,
		eventTypeKey: inquiry.eventType,
      contactId: inquiry.contactId,
      contactFirstName: inquiry.contact?.firstName || '',
      contactLastName: inquiry.contact?.lastName || '',
      quoteTotal: inquiry.quoteTotal,
      createdAt: inquiry.createdAt,
      updatedAt: inquiry.updatedAt,
    }))
    return {
      inquiries: inquiriesLite,
      totalCount: inquiriesLite.length,
    }
  },

  'cortado.v1.CortadoService/GetInquiry': (req: unknown) => {
    const request = req as { id?: string }
    const inquiry = demoInquiries.find((i) => i.id === request.id) || demoInquiries[0]
    return { inquiry }
  },

  // Booking endpoints
  'cortado.v1.CortadoService/GetBooking': (req: unknown) => {
    const request = req as { id?: string }
    const booking = demoBookings.find((b) => b.id === request.id) || demoBookings[0]
    return { booking }
  },

  // Event endpoints
  'cortado.v1.CortadoService/ListEvents': () => ({
    events: demoEvents,
    totalCount: demoEvents.length,
  }),

  'cortado.v1.CortadoService/GetEvent': (req: unknown) => {
    const request = req as { id?: string }
    const event = demoEvents.find((e) => e.id === request.id) || demoEvents[0]
    return { event }
  },

  // Aggregate: event + parent booking + originating inquiry, composed from the
  // same fixtures GetEvent/GetBooking/GetInquiry return individually.
  'cortado.v1.CortadoService/GetEventDetail': (req: unknown) => {
    const request = req as { id?: string }
    const event = demoEvents.find((e) => e.id === request.id) || demoEvents[0]
    const booking = demoBookings.find((b) => b.id === event.bookingId) || demoBookings[0]
    const inquiry = booking.inquiryId
      ? demoInquiries.find((i) => i.id === booking.inquiryId) || demoInquiries[0]
      : undefined
    return { event, booking, inquiry }
  },

  // Invoice endpoints
  'cortado.v1.CortadoService/ListInvoices': () => ({
    invoices: demoInvoices,
    totalCount: demoInvoices.length,
  }),

  'cortado.v1.CortadoService/GetInvoice': (req: unknown) => {
    const request = req as { id?: string }
    const invoice = demoInvoices.find((i) => i.id === request.id) || demoInvoices[0]
    return { invoice }
  },

  'cortado.v1.CortadoService/GetInvoiceLitesByIds': (req: unknown) => {
    const request = req as { ids?: string[] }
    const invoices = request.ids
      ? demoInvoices.filter((i) => request.ids!.includes(i.id))
      : demoInvoices
    return { invoices }
  },

  // Aggregate: invoice + contact + parent booking, composed from the same
  // fixtures the individual GetInvoice/GetContact/GetBooking endpoints return.
  'cortado.v1.CortadoService/GetInvoiceDetail': (req: unknown) => {
    const request = req as { id?: string }
    const invoice = demoInvoices.find((i) => i.id === request.id) || demoInvoices[0]
    const contact = invoice.contactId
      ? demoContacts.find((c) => c.id === invoice.contactId)
      : undefined
    const booking = invoice.bookingId
      ? demoBookings.find((b) => b.id === invoice.bookingId)
      : undefined
    return { invoice, contact, booking }
  },

  // Quote endpoints
  'cortado.v1.CortadoService/ListQuotes': () => ({
    quotes: demoQuotes,
    totalCount: demoQuotes.length,
  }),

  'cortado.v1.CortadoService/GetQuote': (req: unknown) => {
    const request = req as { id?: string }
    const quote = demoQuotes.find((q) => q.id === request.id) || demoQuotes[0]
    return { quote }
  },

  'cortado.v1.CortadoService/GetQuoteLitesByIds': (req: unknown) => {
    const request = req as { ids?: string[] }
    const quotes = request.ids ? demoQuotes.filter((q) => request.ids!.includes(q.id)) : demoQuotes
    return { quotes }
  },

  // Aggregate: quote + originating inquiry + contact + parent booking + the
  // invoice converted from the quote, composed from the same fixtures the
  // individual GetQuote/GetInquiry/GetContact/GetBooking endpoints return.
  'cortado.v1.CortadoService/GetQuoteDetail': (req: unknown) => {
    const request = req as { id?: string }
    const quote = demoQuotes.find((q) => q.id === request.id) || demoQuotes[0]
    const inquiry = quote.inquiryId
      ? demoInquiries.find((i) => i.id === quote.inquiryId)
      : undefined
    const contact = quote.contactId
      ? demoContacts.find((c) => c.id === quote.contactId)
      : undefined
    const booking = inquiry?.bookingId
      ? demoBookings.find((b) => b.id === inquiry.bookingId)
      : undefined
    const convertedInvoice = demoInvoices.find((inv) => inv.quoteId === quote.id)
    return { quote, inquiry, contact, booking, convertedInvoice }
  },

  // Task endpoints
  'cortado.v1.CortadoService/ListTasks': () => ({
    tasks: demoTasks,
    totalCount: demoTasks.length,
  }),

  'cortado.v1.CortadoService/GetTask': (req: unknown) => {
    const request = req as { id?: string }
    const task = demoTasks.find((t) => t.id === request.id) || demoTasks[0]
    return { task }
  },

  'cortado.v1.CortadoService/GetDashboardTasks': () => ({
    tasks: demoTasks.slice(0, 5),
    totalCount: demoTasks.length,
  }),

  // Item (catalog) endpoints
  'cortado.v1.CortadoService/ListItems': () => ({
    items: demoItems,
    totalCount: demoItems.length,
  }),

  'cortado.v1.CortadoService/GetItem': (req: unknown) => {
    const request = req as { id?: string }
    const item = demoItems.find((i) => i.id === request.id) || demoItems[0]
    return { item }
  },

  // Tax rate endpoints
  'cortado.v1.CortadoService/ListTaxRates': () => ({
    taxRates: demoTaxRates,
    totalCount: demoTaxRates.length,
  }),

  // Discount endpoints
  'cortado.v1.CortadoService/ListDiscounts': () => ({
    discounts: demoDiscounts,
    totalCount: demoDiscounts.length,
  }),

  'cortado.v1.CortadoService/GetDiscount': (req: unknown) => {
    const request = req as { id?: string }
    const discount = demoDiscounts.find((d) => d.id === request.id) || demoDiscounts[0]
    return { discount }
  },

  // Dashboard endpoints
  'cortado.v1.CortadoService/GetDashboardUpcomingEvents': () => ({
    events: demoEvents.slice(0, 3),
    totalCount: demoEvents.length,
  }),

  'cortado.v1.CortadoService/GetDashboardBookings': () => ({
    bookings: demoBookings,
    totalCount: demoBookings.length,
  }),

  // Note endpoints
  'cortado.v1.CortadoService/ListNotes': (req: unknown) => {
    const request = req as { parentType?: number; parentId?: string }
    let notes = demoNotes
    if (request.parentType !== undefined && request.parentId) {
      notes = demoNotes.filter(
        (n) => n.parentType === request.parentType && n.parentId === request.parentId
      )
    } else if (request.parentType !== undefined) {
      notes = demoNotes.filter((n) => n.parentType === request.parentType)
    } else if (request.parentId) {
      notes = demoNotes.filter((n) => n.parentId === request.parentId)
    }
    return {
      notes,
      totalCount: notes.length,
    }
  },

  // User scheduling endpoints
  'cortado.v1.CortadoService/ListUserSchedulings': () => ({
    userSchedulings: [],
    totalCount: 0,
  }),

  // Role endpoints
  'cortado.v1.CortadoService/ListOrganizationRoles': () => ({
    roles: [
      {
        id: 'role-admin',
        name: 'Admin',
        organizationId: demoOrganization.id,
        permissions: [],
      },
      {
        id: 'role-staff',
        name: 'Staff',
        organizationId: demoOrganization.id,
        permissions: [],
      },
    ],
    totalCount: 2,
  }),

  'cortado.v1.CortadoService/ListPermissions': () => ({
    permissions: [],
    totalCount: 0,
  }),

  // Ping endpoint
  'cortado.v1.CortadoService/Ping': () => ({
    message: 'pong (demo mode)',
  }),

  // Stripe endpoints (return disconnected state for demo)
  'cortado.v1.CortadoService/GetStripeConnectionStatus': () => ({
    isConnected: true,
    accountId: 'acct_demo123',
    accountType: 'standard',
    chargesEnabled: true,
    payoutsEnabled: true,
    detailsSubmitted: true,
  }),

  // Gmail endpoints
  'cortado.v1.CortadoService/IsGmailConnected': () => ({
    isConnected: true,
    connectedEmails: [
      {
        id: 'gmail-demo-1',
        userId: 'user-demo-1',
        email: 'demo@sunriseeventco.com',
        isDefault: true,
      },
      {
        id: 'gmail-demo-2',
        userId: 'user-demo-2',
        email: 'events@sunriseeventco.com',
        isDefault: false,
      },
    ],
  }),

  'cortado.v1.CortadoService/ListGoogleCalendars': () => ({
    calendars: [
      {
        id: 'primary',
        name: 'Primary',
        gmailAccountId: 'gmail-demo-1',
        gmailAccountEmail: 'demo@sunriseeventco.com',
      },
      {
        id: 'events',
        name: 'Events',
        gmailAccountId: 'gmail-demo-1',
        gmailAccountEmail: 'demo@sunriseeventco.com',
      },
      {
        id: 'catering',
        name: 'Catering Calendar',
        gmailAccountId: 'gmail-demo-2',
        gmailAccountEmail: 'events@sunriseeventco.com',
      },
    ],
    selectedCalendarId: 'events',
    selectedCalendarName: 'Events',
    selectedGmailAccountId: 'gmail-demo-1',
    selectedGmailAccountEmail: 'demo@sunriseeventco.com',
  }),

  'cortado.v1.CortadoService/SetGoogleCalendar': () => ({
    success: true,
  }),

  // Subscription endpoints
  'cortado.v1.CortadoService/GetOrganizationSubscription': () => ({
    subscription: {
      id: 'sub-demo-001',
      status: 'active',
      planName: 'Professional',
      currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    },
  }),

  // Payment endpoints
  'cortado.v1.CortadoService/ListInvoicePayments': () => ({
    stripePayments: [],
    manualPayments: [],
    totalCount: 0,
  }),

  'cortado.v1.CortadoService/ListStripePayments': () => ({
    payments: [],
    totalCount: 0,
  }),

  // Feedback endpoints
  'cortado.v1.CortadoService/ListFeedback': () => ({
    feedback: demoFeedback,
    totalCount: demoFeedback.length,
  }),
}

/**
 * Create a demo interceptor that returns mock data for registered handlers
 */
const createDemoInterceptor = (): Interceptor => {
  return (next) => async (request) => {
    // Only intercept if demo mode is active
    if (!isDemoModeActive()) {
      return next(request)
    }

    const methodPath = `${request.service.typeName}/${request.method.name}`
    const handler = mockHandlers[methodPath]

    if (handler) {
      // Return mock data
      const mockResponse = handler(request.message)

      // Create a response that matches the expected shape
      // Type assertion needed because mock handlers return unknown
      return {
        stream: false as const,
        service: request.service,
        method: request.method,
        header: new Headers(),
        trailer: new Headers(),
        message: mockResponse,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any
    }

    // If no mock handler, let the request through (will fail without backend)
    // In full demo mode, you might want to return a generic error instead
    console.warn(`[Demo Mode] No mock handler for: ${methodPath}`)
    return next(request)
  }
}

/**
 * Create a Connect transport with demo mode support
 *
 * When demo mode is active (VITE_DEMO_MODE_ENABLED=true && ?demo=true),
 * API calls are intercepted and return mock data.
 */
export function createDemoTransport(options: ConnectTransportOptions): Transport {
  const demoInterceptor = createDemoInterceptor()

  return createConnectTransport({
    ...options,
    interceptors: [demoInterceptor, ...(options.interceptors || [])],
  })
}
